import React, { useRef, useState } from 'react';
import { api } from '../api';
import { useToast } from '../ToastContext';

// ── lineStatus chip helper ────────────────────────────────────────────────
const STATUS_CHIP = {
  New:            'muted',
  Dispatched:     'info',
  PartialQuotes:  'review',
  AllQuoted:      'ready',
  Decided:        'primary',
  CustomerQuoted: 'ready',
  Won:            'ready',
  Lost:           'risk',
};

function LineStatusChip({ status }) {
  if (!status || status === 'New') return <span className="chip muted">New</span>;
  return <span className={'chip ' + (STATUS_CHIP[status] || 'muted')}>{status}</span>;
}

const CONDITIONS = ['AnyNew', 'FactoryNew', 'NewSurplus', 'DistributorStock', 'NewOldStock', 'Other'];

// Sensible aerospace defaults — matches the most common customer asks.
// Non-aerospace / commercial lines are uncommon; unchecking is easy.
function blankLine() {
  return {
    partNumber: '',
    description: '',
    quantity: 1,
    approvedManufacturers: '',
    qplRequired: true,
    cocRequired: true,
    dualCocRequired: false,
    alternateAllowed: false,
    conditionRequired: 'FactoryNew',
    approvedManufacturerMandatory: false,
    notes: '',
  };
}

/**
 * Aerospace requirements editor (#16).
 *
 * Eleven more columns would have made an already-wide table unusable, so these
 * live in a per-line panel opened from the row. Each requirement here is a
 * receiving-dock rejection if it goes unchecked.
 */
function AeroRequirements({ line, onSave, onClose }) {
  const [f, setF] = useState({
    dfarsRequired: !!line.dfarsRequired,
    specialtyMetalsCompliance: !!line.specialtyMetalsCompliance,
    approvedCountriesOfOrigin: (line.approvedCountriesOfOrigin || []).join(', '),
    itarControlled: !!line.itarControlled,
    earControlled: !!line.earControlled,
    exportControlNotes: line.exportControlNotes || '',
    minShelfLifeRemainingMonths: line.minShelfLifeRemainingMonths ?? '',
    maxDateCodeAgeMonths: line.maxDateCodeAgeMonths ?? '',
    lotTraceabilityRequired: !!line.lotTraceabilityRequired,
    firstArticleInspectionRequired: !!line.firstArticleInspectionRequired,
    sourceInspectionRequired: !!line.sourceInspectionRequired,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const check = (k, label) => (
    <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
      <input type="checkbox" checked={f[k]} aria-label={label} onChange={(e) => set(k, e.target.checked)} />
      {label}
    </label>
  );

  async function submit() {
    setBusy(true);
    try {
      await onSave({
        dfarsRequired: f.dfarsRequired,
        specialtyMetalsCompliance: f.specialtyMetalsCompliance,
        approvedCountriesOfOrigin: f.approvedCountriesOfOrigin
          .split(/[,;]/).map((x) => x.trim().toUpperCase()).filter(Boolean),
        itarControlled: f.itarControlled,
        earControlled: f.earControlled,
        exportControlNotes: f.exportControlNotes || undefined,
        // Blank must clear the limit, not send NaN.
        minShelfLifeRemainingMonths: f.minShelfLifeRemainingMonths === '' ? null : Number(f.minShelfLifeRemainingMonths),
        maxDateCodeAgeMonths: f.maxDateCodeAgeMonths === '' ? null : Number(f.maxDateCodeAgeMonths),
        lotTraceabilityRequired: f.lotTraceabilityRequired,
        firstArticleInspectionRequired: f.firstArticleInspectionRequired,
        sourceInspectionRequired: f.sourceInspectionRequired,
      });
    } finally { setBusy(false); }
  }

  return (
    <tr>
      <td colSpan={14} style={{ background: 'var(--surface-1)', padding: 16 }}>
        <b style={{ fontSize: 13 }}>Aerospace requirements — line {line.lineNumber} · {line.partNumber}</b>
        <div className="subtitle" style={{ marginBottom: 10 }}>
          Saving re-scores every quote already captured against this line.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 10 }}>
          {check('dfarsRequired', 'DFARS required')}
          {check('specialtyMetalsCompliance', 'Specialty metals compliance')}
          {check('itarControlled', 'ITAR controlled')}
          {check('earControlled', 'EAR controlled')}
          {check('lotTraceabilityRequired', 'Lot traceability required')}
          {check('firstArticleInspectionRequired', 'First-article inspection')}
          {check('sourceInspectionRequired', 'Source inspection')}
          <label className="field"><span className="label">Approved countries of origin</span>
            <input className="input mono" aria-label="Approved countries of origin"
              placeholder="US, GB, DE" value={f.approvedCountriesOfOrigin}
              onChange={(e) => set('approvedCountriesOfOrigin', e.target.value)} />
          </label>
          <label className="field"><span className="label">Min shelf life (months)</span>
            <input className="input mono" type="number" aria-label="Minimum shelf life remaining months"
              value={f.minShelfLifeRemainingMonths}
              onChange={(e) => set('minShelfLifeRemainingMonths', e.target.value)} />
          </label>
          <label className="field"><span className="label">Max date-code age (months)</span>
            <input className="input mono" type="number" aria-label="Maximum date code age months"
              value={f.maxDateCodeAgeMonths}
              onChange={(e) => set('maxDateCodeAgeMonths', e.target.value)} />
          </label>
          <label className="field"><span className="label">Export control notes</span>
            <input className="input" aria-label="Export control notes"
              value={f.exportControlNotes} onChange={(e) => set('exportControlNotes', e.target.value)} />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn btn-sm" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : 'Save requirements'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
        </div>
      </td>
    </tr>
  );
}

/** Compact summary of which aerospace requirements a line imposes. */
function AeroChips({ l }) {
  const on = [];
  if (l.dfarsRequired) on.push('DFARS');
  if (l.specialtyMetalsCompliance) on.push('Spec metals');
  if (l.itarControlled) on.push('ITAR');
  if (l.earControlled) on.push('EAR');
  if ((l.approvedCountriesOfOrigin || []).length) on.push(`Origin: ${l.approvedCountriesOfOrigin.join('/')}`);
  if (l.minShelfLifeRemainingMonths != null) on.push(`Shelf ${l.minShelfLifeRemainingMonths}m`);
  if (l.maxDateCodeAgeMonths != null) on.push(`DC <${l.maxDateCodeAgeMonths}m`);
  if (l.lotTraceabilityRequired) on.push('Lot trace');
  if (l.firstArticleInspectionRequired) on.push('FAI');
  if (l.sourceInspectionRequired) on.push('Src insp');
  if (on.length === 0) return <span style={{ color: 'var(--ink-500)', fontSize: 11 }}>—</span>;
  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
      {on.map((t) => <span key={t} className="chip muted" style={{ fontSize: 10 }}>{t}</span>)}
    </span>
  );
}

export default function Structuring({ data, reload, selectedLineIds = [], onSelectionChange }) {
  const toast = useToast();
  const [draft, setDraft] = useState([blankLine()]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {lines, errors}
  // Which line's aerospace-requirements panel is open (#16).
  const [aeroLineId, setAeroLineId] = useState(null);
  const fileRef = useRef(null);

  // ── Line selection ────────────────────────────────────────────────────
  const [checkedIds, setCheckedIds] = useState(new Set(selectedLineIds));

  function toggleLine(id) {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (checkedIds.size === data.lines.length) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(data.lines.map((l) => String(l._id))));
    }
  }

  function sendSelection() {
    const ids = [...checkedIds];
    if (ids.length === 0) return;
    if (onSelectionChange) onSelectionChange(ids);
  }

  function setField(i, k, v) {
    setDraft((d) => d.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  }

  function addRow() { setDraft((d) => [...d, blankLine()]); }
  function remove(i) { setDraft((d) => d.filter((_, idx) => idx !== i)); }

  // Paste-from-Excel into the first cell: splits by newlines + tabs.
  function handlePaste(i, field, ev) {
    const text = ev.clipboardData?.getData('text') || '';
    if (!text.includes('\t') && !text.includes('\n')) return; // single cell paste — default behavior
    ev.preventDefault();
    const rows = text.split(/\r?\n/).filter((r) => r.length);
    if (rows.length === 0) return;
    setDraft((d) => {
      const next = d.slice();
      rows.forEach((r, idx) => {
        const cols = r.split('\t');
        const rowIdx = i + idx;
        const row = next[rowIdx] || blankLine();
        const [partNumber, qty, mfrs, qpl, cond, coc, alt] = cols;
        if (partNumber !== undefined) row.partNumber = partNumber.trim();
        if (qty !== undefined) row.quantity = Number(qty) || 1;
        if (mfrs !== undefined) row.approvedManufacturers = mfrs.trim();
        if (qpl !== undefined) row.qplRequired = /^(y|yes|1|true)$/i.test(qpl.trim());
        if (cond !== undefined && cond.trim()) row.conditionRequired = cond.trim();
        if (coc !== undefined) row.cocRequired = /^(y|yes|1|true)$/i.test(coc.trim());
        if (alt !== undefined) row.alternateAllowed = /^(y|yes|1|true)$/i.test(alt.trim());
        next[rowIdx] = row;
      });
      return next;
    });
    toast.push(`Pasted ${rows.length} row(s)`);
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await api.uploadRfqFile(data.rfq._id, file);
      setPreview(res);
      toast.push(`Parsed ${res.preview.length} rows · ${res.errors.length} errors`);
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function commitPreview() {
    if (!preview?.preview) return;
    const clean = preview.preview.filter((p) => p.partNumber && p.quantity >= 1);
    if (clean.length === 0) { toast.push('Nothing to commit — all rows have errors', { variant: 'error' }); return; }
    setBusy(true);
    try {
      await api.addLines(data.rfq._id, clean);
      toast.push(`Added ${clean.length} lines from Excel`);
      setPreview(null);
      reload();
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    } finally { setBusy(false); }
  }

  async function save() {
    setBusy(true);
    try {
      const payload = draft.map((r) => ({
        partNumber: r.partNumber.trim(),
        description: r.description,
        quantity: Number(r.quantity) || 1,
        approvedManufacturers: r.approvedManufacturers.split(/[,;]/).map((s) => s.trim()).filter(Boolean),
        qplRequired: r.qplRequired,
        cocRequired: r.cocRequired,
        dualCocRequired: r.dualCocRequired,
        alternateAllowed: r.alternateAllowed,
        conditionRequired: r.conditionRequired,
        approvedManufacturerMandatory: r.approvedManufacturerMandatory,
        notes: r.notes,
      }));
      await api.addLines(data.rfq._id, payload);
      toast.push('Lines added');
      setDraft([blankLine()]);
      reload();
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function removeExisting(id) {
    await api.deleteLine(data.rfq._id, id);
    reload();
  }

  // ── Inline edit of an existing (already-extracted) line ─────────────────
  // The lines pulled from the email are otherwise read-only; this lets the
  // user correct an extraction (wrong qty, missing mfr, etc.) in place.
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState(null);

  function startEdit(l) {
    setEditId(String(l._id));
    setEditRow({
      partNumber: l.partNumber || '',
      quantity: l.quantity ?? 1,
      approvedManufacturers: (l.approvedManufacturers || []).join(', '),
      conditionRequired: l.conditionRequired || 'AnyNew',
      qplRequired: !!l.qplRequired,
      cocRequired: !!l.cocRequired,
      dualCocRequired: !!l.dualCocRequired,
      approvedManufacturerMandatory: !!l.approvedManufacturerMandatory,
      alternateAllowed: !!l.alternateAllowed,
    });
  }
  function cancelEdit() { setEditId(null); setEditRow(null); }

  /** Save the aerospace requirement set; the API re-scores captured quotes. */
  async function saveAero(lineId, patch) {
    try {
      const r = await api.updateLine(data.rfq._id, lineId, patch);
      const n = r?.requalified?.changed;
      toast.push(n ? `Requirements saved — ${n} quote line(s) re-scored` : 'Requirements saved');
      setAeroLineId(null);
      reload();
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    }
  }
  function setEditField(k, v) { setEditRow((r) => ({ ...r, [k]: v })); }

  async function saveEdit(id) {
    setBusy(true);
    try {
      await api.updateLine(data.rfq._id, id, {
        partNumber: editRow.partNumber.trim(),
        quantity: Number(editRow.quantity) || 1,
        approvedManufacturers: editRow.approvedManufacturers.split(/[,;]/).map((s) => s.trim()).filter(Boolean),
        conditionRequired: editRow.conditionRequired,
        qplRequired: editRow.qplRequired,
        cocRequired: editRow.cocRequired,
        dualCocRequired: editRow.dualCocRequired,
        approvedManufacturerMandatory: editRow.approvedManufacturerMandatory,
        alternateAllowed: editRow.alternateAllowed,
      });
      toast.push(`Line ${id.slice(-6)} updated`);
      cancelEdit();
      reload();
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  const emailBody = data?.rfq?.originalEmailBody;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: emailBody ? '40% 60%' : '1fr',
        gap: 20,
        alignItems: 'start',
      }}
      className="structuring-split"
    >
      {emailBody && (
        <div
          className="card section-bg"
          style={{
            overflowY: 'auto',
            maxHeight: '80vh',
            fontFamily: 'monospace',
            fontSize: 12,
            whiteSpace: 'pre-wrap',
            lineHeight: 1.5,
            position: 'sticky',
            top: 16,
          }}
        >
          <h3 className="card-title" style={{ fontFamily: 'var(--font-sans, sans-serif)', marginBottom: 8 }}>
            Original email body
          </h3>
          {emailBody}
        </div>
      )}
      {!emailBody && (
        <div style={{ display: 'none' }} />
      )}
      <div>
      <div className="card section-bg" style={{ marginBottom: 20, display: 'flex', gap: 16, alignItems: 'center' }}>
        <div>
          <b>Bulk add from Excel / CSV</b>
          <div className="subtitle">Use the Rivet template, or paste rows directly into the table below (multi-cell paste supported).</div>
        </div>
        <div style={{ flex: 1 }} />
        <a className="btn btn-secondary btn-sm" href={api.templateUrl()}>Download template</a>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} style={{ display: 'none' }} />
        <button className="btn btn-sm" onClick={() => fileRef.current?.click()}>Upload file</button>
      </div>

      {preview && (
        <div className="card" style={{ marginBottom: 20, borderColor: 'var(--primary)' }}>
          <h3 className="card-title">Preview — {preview.preview.length} rows, {preview.errors.length} errors</h3>
          {preview.errors.length > 0 && (
            <div className="chip risk" style={{ marginBottom: 8 }}>
              {preview.errors.length} rows have errors — they'll be skipped
            </div>
          )}
          <table className="table" style={{ marginTop: 8 }}>
            <thead><tr><th>#</th><th>Part</th><th>Qty</th><th>Mfrs</th><th>QPL</th><th>Cond</th><th>CoC</th><th>Errors</th></tr></thead>
            <tbody>
              {preview.preview.slice(0, 20).map((p, idx) => {
                const rowErrors = preview.errors[idx]?.errors || [];
                return (
                  <tr key={idx}>
                    <td>{p.lineNumber}</td>
                    <td className="mono">{p.partNumber}</td>
                    <td className="mono">{p.quantity}</td>
                    <td>{(p.approvedManufacturers || []).join(', ')}</td>
                    <td>{p.qplRequired ? 'Y' : 'N'}</td>
                    <td>{p.conditionRequired}</td>
                    <td>{p.cocRequired ? 'Y' : 'N'}</td>
                    <td style={{ color: 'var(--danger)', fontSize: 12 }}>{rowErrors.join('; ')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {preview.preview.length > 20 && <div className="subtitle">…and {preview.preview.length - 20} more</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-secondary" onClick={() => setPreview(null)}>Discard</button>
            <div style={{ flex: 1 }} />
            <button className="btn" onClick={commitPreview} disabled={busy}>{busy ? 'Saving…' : `Commit ${preview.preview.filter((p) => p.partNumber && p.quantity >= 1).length} valid rows`}</button>
          </div>
        </div>
      )}

      <h2 className="card-title">RFQ line items</h2>

      {/* Floating toolbar when lines are selected */}
      {checkedIds.size > 0 && (
        <div style={{
          position: 'sticky', top: 8, zIndex: 50, display: 'flex', alignItems: 'center', gap: 12,
          background: 'var(--primary)', color: '#fff', padding: '10px 16px', borderRadius: 10,
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)', marginBottom: 12,
        }}>
          <span style={{ fontWeight: 600 }}>{checkedIds.size} line{checkedIds.size !== 1 ? 's' : ''} selected</span>
          <div style={{ flex: 1 }} />
          <button
            className="btn btn-sm"
            style={{ background: '#fff', color: 'var(--primary)', fontWeight: 700 }}
            onClick={sendSelection}
          >
            Send selection to suppliers ({checkedIds.size} lines) →
          </button>
          <button className="btn btn-sm btn-ghost" style={{ color: '#fff', border: '1px solid rgba(255,255,255,0.4)' }} onClick={() => setCheckedIds(new Set())}>
            Clear
          </button>
        </div>
      )}

      <div className="card" style={{ marginBottom: 20 }}>
        <b>Existing lines: {data.lines.length}</b>
        {data.lines.length > 0 && (
          <table className="table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    checked={data.lines.length > 0 && checkedIds.size === data.lines.length}
                    onChange={toggleAll}
                    title="Select all"
                    aria-label="Select all lines"
                  />
                </th>
                <th>#</th><th>Status</th><th>Part No</th><th>Qty</th><th>Mfr List</th>
                {/* Mandatory-Mfr and Dual-CoC were settable only in "Add lines",
                    so a line that arrived by Excel or AI extraction could never
                    be corrected here (finding #02). */}
                <th title="Approved manufacturer is mandatory">Mand Mfr</th>
                <th>Condition</th><th>QPL</th><th>CoC</th><th title="Dual CoC required">Dual CoC</th><th>Alt</th><th></th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((l) => {
                const isEditing = editId === String(l._id);
                if (isEditing) {
                  return (
                    <tr key={l._id} style={{ background: 'var(--primary-tint)' }}>
                      <td />
                      <td>{l.lineNumber}</td>
                      <td><LineStatusChip status={l.lineStatus} /></td>
                      <td><input className="input mono" value={editRow.partNumber} onChange={(e) => setEditField('partNumber', e.target.value)} aria-label="Part number" /></td>
                      <td><input className="input" type="number" min="1" value={editRow.quantity} onChange={(e) => setEditField('quantity', e.target.value)} style={{ width: 72 }} aria-label="Quantity" /></td>
                      <td><input className="input" value={editRow.approvedManufacturers} onChange={(e) => setEditField('approvedManufacturers', e.target.value)} placeholder="comma-separated" aria-label="Manufacturer list" /></td>
                      <td><input type="checkbox" checked={!!editRow.approvedManufacturerMandatory} onChange={(e) => setEditField('approvedManufacturerMandatory', e.target.checked)} aria-label="Approved manufacturer mandatory" /></td>
                      <td>
                        <select className="select" value={editRow.conditionRequired} onChange={(e) => setEditField('conditionRequired', e.target.value)} aria-label="Condition">
                          {CONDITIONS.map((c) => <option key={c}>{c}</option>)}
                        </select>
                      </td>
                      <td><input type="checkbox" checked={editRow.qplRequired} onChange={(e) => setEditField('qplRequired', e.target.checked)} aria-label="QPL required" /></td>
                      <td><input type="checkbox" checked={editRow.cocRequired} onChange={(e) => setEditField('cocRequired', e.target.checked)} aria-label="CoC required" /></td>
                      <td><input type="checkbox" checked={!!editRow.dualCocRequired} onChange={(e) => setEditField('dualCocRequired', e.target.checked)} aria-label="Dual CoC required" /></td>
                      <td><input type="checkbox" checked={editRow.alternateAllowed} onChange={(e) => setEditField('alternateAllowed', e.target.checked)} aria-label="Alternate allowed" /></td>
                      {/* Aerospace requirements are edited in their own panel. */}
                      <td><AeroChips l={l} /></td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn btn-sm" onClick={() => saveEdit(String(l._id))} disabled={busy || !editRow.partNumber.trim()}>{busy ? 'Saving…' : 'Save'}</button>
                        <button className="btn btn-ghost btn-sm" onClick={cancelEdit} disabled={busy}>Cancel</button>
                      </td>
                    </tr>
                  );
                }
                const row = (
                  <tr key={l._id} style={{ background: checkedIds.has(String(l._id)) ? 'var(--primary-tint)' : undefined }}>
                    <td>
                      <input type="checkbox" checked={checkedIds.has(String(l._id))} onChange={() => toggleLine(String(l._id))} aria-label={`Select line ${l.lineNumber} ${l.partNumber}`} />
                    </td>
                    <td>{l.lineNumber}</td>
                    <td><LineStatusChip status={l.lineStatus} /></td>
                    <td className="mono">{l.partNumber}</td>
                    <td>{l.quantity}</td>
                    <td>{(l.approvedManufacturers || []).join(', ')}</td>
                    <td>{l.approvedManufacturerMandatory ? 'Yes' : 'No'}</td>
                    <td>{l.conditionRequired}</td>
                    <td>{l.qplRequired ? 'Yes' : 'No'}</td>
                    <td>{l.cocRequired ? 'Yes' : 'No'}</td>
                    <td>{l.dualCocRequired ? 'Yes' : 'No'}</td>
                    <td>{l.alternateAllowed ? 'Yes' : 'No'}</td>
                    <td><AeroChips l={l} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => startEdit(l)} disabled={!!editId}>Edit</button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setAeroLineId(aeroLineId === String(l._id) ? null : String(l._id))}
                        disabled={!!editId}
                        title="DFARS, country of origin, ITAR/EAR, shelf life, traceability, inspection"
                      >
                        Aero
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => removeExisting(l._id)} disabled={!!editId}>Delete</button>
                    </td>
                  </tr>
                );
                // The aerospace panel is a sibling row so it can span the table.
                return aeroLineId === String(l._id) ? (
                  <React.Fragment key={l._id}>
                    {row}
                    <AeroRequirements
                      line={l}
                      onSave={(patch) => saveAero(l._id, patch)}
                      onClose={() => setAeroLineId(null)}
                    />
                  </React.Fragment>
                ) : row;
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <b>Add lines</b>
        <div className="subtitle">
          Paste from Excel uses this column order: Part No · Qty · Mfr list · QPL · Condition · CoC · Alt.
        </div>
        <table className="table" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>Part No</th><th>Qty</th><th>Mfr list (comma)</th><th>Condition</th><th>QPL</th><th>CoC</th><th>Dual CoC</th><th>Alt</th><th>Mand Mfr</th><th></th>
            </tr>
          </thead>
          <tbody>
            {draft.map((r, i) => (
              <tr key={i}>
                <td><input className="input" value={r.partNumber} onChange={(e) => setField(i, 'partNumber', e.target.value)} onPaste={(e) => handlePaste(i, 'partNumber', e)} /></td>
                <td><input className="input" type="number" min="1" value={r.quantity} onChange={(e) => setField(i, 'quantity', e.target.value)} style={{ width: 80 }} /></td>
                <td><input className="input" value={r.approvedManufacturers} onChange={(e) => setField(i, 'approvedManufacturers', e.target.value)} /></td>
                <td>
                  <select className="select" value={r.conditionRequired} onChange={(e) => setField(i, 'conditionRequired', e.target.value)}>
                    {CONDITIONS.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </td>
                {[
                  ['qplRequired', 'QPL required'],
                  ['cocRequired', 'CoC required'],
                  ['dualCocRequired', 'Dual CoC required'],
                  ['alternateAllowed', 'Alternate allowed'],
                  ['approvedManufacturerMandatory', 'Approved manufacturer mandatory'],
                ].map(([f, fieldLabel]) => (
                  <td key={f}><input type="checkbox" checked={r[f]} onChange={(e) => setField(i, f, e.target.checked)} aria-label={`${fieldLabel} for row ${i + 1}`} /></td>
                ))}
                <td><button className="btn btn-ghost btn-sm" onClick={() => remove(i)} disabled={draft.length <= 1}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn btn-secondary" onClick={addRow}>+ Add row</button>
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={save} disabled={busy || draft.every((r) => !r.partNumber)}>{busy ? 'Saving…' : 'Save lines'}</button>
        </div>
      </div>
      </div>
    </div>
  );
}
